import { addDays, getUnixTime } from "date-fns";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ReactECharts } from "../../../components/Chart";
import { Button, ButtonWrap } from "../../../components/CTA";
import { Dropdown } from "../../../components/Dropdown";
import { Flex } from "../../../components/Flex";
import { InfoIcon } from "../../../components/icons/InfoIcon";
import Loader from "../../../components/Loader";
import { Underline } from "../../../components/Tabs";
import { Text } from "../../../components/Text";
import { FakeTextLink } from "../../../components/TextLink";
import { View } from "../../../components/View";
import { useGetBrandApprovalResponseTimesQuery } from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { useTheme } from "../../../hooks/useTheme";
import { authSelectors } from "../../../store/auth/selector";
import styled from "../../../styles";

const StyledFlex = styled(Flex)`
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

interface Props {
  onClose: () => void;
  onConfirm: () => void;
}

const timePeriodOptions: { label: string; value: number }[] = [
  { label: "Last 7 days", value: 7 },
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 },
];

export const ApprovalTimeModal = (props: Props) => {
  const client = useGqlClient();
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const brand = useSelector(authSelectors.activeBrand);
  const [timePeriod, setTimePeriod] = useState(timePeriodOptions[2].value);
  const today = useMemo(() => new Date(), []);
  const history = useHistory();
  const { data, isLoading } = useGetBrandApprovalResponseTimesQuery(
    client,
    {
      id: activeBrandId || "",
      startDate: getUnixTime(addDays(today, -timePeriod)),
      filters: {
        startDate: getUnixTime(addDays(today, -timePeriod)),
      },
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  const theme = useTheme();

  const numberFormat = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  });
  const formatDuration = (hours: number) => {
    if (hours >= 24) {
      const days = hours / 24;
      const roundedDays = Math.round(days * 2) / 2; // nearest 0.5 day
      const value = numberFormat.format(roundedDays);
      const isSingular = Math.abs(roundedDays - 1) < 1e-9;
      return `${value} ${isSingular ? "day" : "days"}`;
    }
    if (hours === 1) {
      return "< 1 hour";
    }
    const value = numberFormat.format(hours);
    const isSingular = Math.abs(hours - 1) < 0.5;
    return `${value} ${isSingular ? "hour" : "hours"}`;
  };

  if (isLoading || !data) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: 100 }}>
        <Loader />
      </Flex>
    );
  }

  const currentData = data.brand?.approvalResponseTimeAverage ?? 0;
  const benchmarkData = data.brand?.approvalResponseTimeBenchmarkAverage ?? 0;
  const topSegmentBenchmarkData =
    data.brand?.approvalResponseTimeBenchmarkTopSegment ?? 0;

  // Treat 0 as "no data" for chart bars so we don't render a misleading zero-length bar
  const brandBarValue = currentData === 0 ? null : currentData;
  const benchmarkBarValue = benchmarkData === 0 ? null : benchmarkData;
  const topSegmentBarValue =
    topSegmentBenchmarkData === 0 ? null : topSegmentBenchmarkData;

  const isFast = currentData <= topSegmentBenchmarkData;

  const timesFasterThanPlatformAverage =
    benchmarkData === 0
      ? 0
      : Math.round((benchmarkData / currentData) * 100) / 100;

  const barColor = isFast
    ? theme.color.stats.neutralValue
    : theme.color.warning;

  const expiredBookings = data.bookingsMetrics?.totalExpiredBookings ?? 0;

  return (
    <>
      <StyledFlex>
        <Flex align="center" justify="space-between">
          <div>
            <Text
              weight="bold"
              size="xl"
              margin={"0 0 0 0"}
              colorPreset="heading"
            >
              Approval Time
            </Text>
            <Underline selected />
          </div>
          <Dropdown
            options={timePeriodOptions.map((opt) => ({
              label: opt.label,
              value: opt.value.toString(),
            }))}
            disableOptionSort={true}
            selectedOptions={[timePeriod.toString()]}
            setSelectedOptions={(options) => {
              setTimePeriod(Number(options[0]));
            }}
            anchor="bottom end"
            renderLabel={() => {
              return (
                timePeriodOptions.find((option) => option.value === timePeriod)
                  ?.label ?? ""
              );
            }}
            size="s"
            selectionMode="single"
          />
        </Flex>
        {currentData === 0 ? (
          <Text margin={"m 0 0 0"} colorPreset="secondary" isInline>
            No bookings have been approved in the{" "}
            {timePeriodOptions
              .find((option) => option.value === timePeriod)
              ?.label.toLowerCase() ?? "last 30 days"}
            , check back again after your next approval.
          </Text>
        ) : isFast ? (
          <Text margin={"m 0 0 0"} colorPreset="secondary" isInline>
            Nice work! Your approval time is {timesFasterThanPlatformAverage}x
            faster than the platform average
          </Text>
        ) : (
          <Text margin={"m 0 0 0"} colorPreset="secondary" isInline>
            A few quick approvals would put you in the top 25%, where brands
            typically receive{" "}
            <span
              style={{ fontWeight: 600, color: theme.color.typography.text }}
            >
              30% more applications
            </span>{" "}
            than the platform average.
          </Text>
        )}

        <View margin="m 0">
          <View padding="0 l">
            <ReactECharts
              option={{
                tooltip: {
                  trigger: "axis",
                  axisPointer: { type: "shadow" },
                  formatter: (params: any) => {
                    const p = Array.isArray(params) ? params[0] : params;
                    const val = p?.value;
                    if (val == null) {
                      return `<b>${p.name}</b><br/>No data`;
                    }
                    return `<b>${p.name}</b><br/>${formatDuration(val)}`;
                  },
                },
                grid: {
                  left: 0,
                  right: 22,
                  top: "10%",
                  bottom: "10%",
                  containLabel: true,
                },
                xAxis: {
                  type: "value",
                  min: 0,
                  axisLabel: { show: false },
                  splitLine: { show: false },
                  axisLine: { show: false },
                  axisTick: { show: false },
                },
                yAxis: {
                  type: "category",
                  data: [
                    brand?.name ?? "Your Brand",
                    "Joli Platform Average",
                    "Joli Platform Top 25%",
                  ],
                  axisTick: { show: false },
                  axisLabel: {
                    show: true,
                    margin: 10,
                    fontWeight: "bold",
                    fontSize: 12,
                    color: theme.color.typography.text,
                  },
                  splitLine: { show: false },
                  axisLine: {
                    show: true,
                    lineStyle: { color: theme.color.typography.secondary },
                  },
                },
                series: [
                  {
                    type: "bar",
                    animationEasing: "sinusoidalInOut",
                    animationDuration: 800,
                    data: [
                      brandBarValue,
                      benchmarkBarValue,
                      topSegmentBarValue,
                    ],
                    label: {
                      show: true,
                      position: "right",
                      formatter: (params: any) => {
                        return formatDuration(params.value);
                      },
                      color: theme.color.typography.text,
                    },
                    itemStyle: {
                      color: (params: any) => {
                        if (params.dataIndex === 0) return barColor;
                        if (params.dataIndex === 1)
                          return theme.color.secondary;
                        return theme.color.stats.positiveValue;
                      },
                    },
                    barWidth: "80%",
                  },
                ],
              }}
            />
          </View>
        </View>

        {expiredBookings > 0 ? (
          <CalloutWrap margin="m 0 m" padding="s l">
            <Flex align="center">
              <Flex align="center" justify="center" margin="0 m 0 0">
                <InfoIcon colorPreset="text" width={26} />
              </Flex>
              <View>
                <Text margin="0" size="s" style={{ marginTop: 2 }}>
                  You missed out on{" "}
                  <span style={{ fontWeight: 600 }}>
                    {expiredBookings === 1
                      ? "1 application"
                      : `${expiredBookings} applications`}{" "}
                  </span>
                  which have expired. These can be rescheduled from your{" "}
                  <FakeTextLink
                    size="s"
                    onClick={() => {
                      history.push("/b/bookings/archived");
                      props.onClose();
                    }}
                  >
                    archived bookings
                  </FakeTextLink>
                </Text>
              </View>
            </Flex>
          </CalloutWrap>
        ) : null}

        <ButtonWrap margin="l 0 0">
          <Button buttonType="secondary" onClick={props.onConfirm}>
            Learn more
          </Button>
          <Button
            onClick={() => {
              props.onConfirm();
            }}
          >
            Got it
          </Button>
        </ButtonWrap>
      </StyledFlex>
    </>
  );
};

const CalloutWrap = styled(View)`
  background-color: ${(p) => p.theme.color.card.callout};
  border-radius: ${(p) => p.theme.misc.borderRadius};
`;
